/**
 * @tony/nl-babylon
 *
 * BabylonJS integration package for React applications.
 * Provides manager-based architecture for 3D rendering.
 */

export const version = "0.0.1";

export type { EntityConfig, PartialBabylonConfig } from "./config/schemas";
// Configuration schemas and types
export {
  BabylonConfigSchema,
  defaultBabylonConfig,
  EntityConfigSchema,
  PartialBabylonConfigSchema,
  safeValidateBabylonConfig,
  safeValidatePartialBabylonConfig,
  validateBabylonConfig,
  validatePartialBabylonConfig,
} from "./config/schemas";
export type {
  ArcRotateCameraConfig,
  CameraConfig,
  FreeCameraConfig,
  UniversalCameraConfig,
} from "./config/schemas/camera.schema";
// Camera schemas
export {
  ArcRotateCameraConfigSchema,
  CameraConfigSchema,
  defaultArcRotateCameraConfig,
  defaultFreeCameraConfig,
  defaultUniversalCameraConfig,
  FreeCameraConfigSchema,
  isArcRotateCameraConfig,
  isFreeCameraConfig,
  isUniversalCameraConfig,
  safeValidateCameraConfig,
  UniversalCameraConfigSchema,
  validateCameraConfig,
} from "./config/schemas/camera.schema";
export type {
  EngineConfig,
  NullEngineConfig,
  WebGLEngineConfig,
  WebGPUEngineConfig,
} from "./config/schemas/engine.schema";
// Engine schemas
export {
  defaultNullEngineConfig,
  defaultWebGLEngineConfig,
  defaultWebGPUEngineConfig,
  EngineConfigSchema,
  isNullEngineConfig,
  isWebGLEngineConfig,
  isWebGPUEngineConfig,
  NullEngineConfigSchema,
  safeValidateEngineConfig,
  validateEngineConfig,
  WebGLEngineConfigSchema,
  WebGPUEngineConfigSchema,
} from "./config/schemas/engine.schema";
export type {
  DirectionalLightConfig,
  HemisphericLightConfig,
  LightConfig,
  PointLightConfig,
  SpotLightConfig,
} from "./config/schemas/light.schema";
// Light schemas
export {
  DirectionalLightConfigSchema,
  defaultDirectionalLightConfig,
  defaultHemisphericLightConfig,
  defaultPointLightConfig,
  defaultSpotLightConfig,
  HemisphericLightConfigSchema,
  isDirectionalLightConfig,
  isHemisphericLightConfig,
  isPointLightConfig,
  isSpotLightConfig,
  LightConfigSchema,
  PointLightConfigSchema,
  SpotLightConfigSchema,
  safeValidateLightConfig,
  validateLightConfig,
} from "./config/schemas/light.schema";
export type {
  MaterialConfig as MaterialConfigType,
  PBRMaterialConfig as PBRMaterialConfigType,
  StandardMaterialConfig as StandardMaterialConfigType,
} from "./config/schemas/material.schema";
// Material schemas
export {
  defaultPBRMaterialConfig,
  defaultStandardMaterialConfig,
  isPBRMaterialConfig,
  isStandardMaterialConfig,
  MaterialConfigSchema,
  PBRMaterialConfigSchema,
  StandardMaterialConfigSchema,
  safeValidateMaterialConfig,
  validateMaterialConfig,
} from "./config/schemas/material.schema";
export type {
  BoxMeshConfig,
  CylinderMeshConfig,
  GroundMeshConfig,
  MeshConfig as MeshConfigType,
  PlaneMeshConfig,
  SphereMeshConfig,
  TorusMeshConfig,
} from "./config/schemas/mesh.schema";
// Mesh schemas
export {
  BoxMeshConfigSchema,
  CylinderMeshConfigSchema,
  defaultBoxMeshConfig,
  defaultCylinderMeshConfig,
  defaultGroundMeshConfig,
  defaultPlaneMeshConfig,
  defaultSphereMeshConfig,
  defaultTorusMeshConfig,
  GroundMeshConfigSchema,
  isBoxMeshConfig,
  isCylinderMeshConfig,
  isGroundMeshConfig,
  isPlaneMeshConfig,
  isSphereMeshConfig,
  isTorusMeshConfig,
  MeshConfigSchema,
  PlaneMeshConfigSchema,
  SphereMeshConfigSchema,
  safeValidateMeshConfig,
  TorusMeshConfigSchema,
  validateMeshConfig,
} from "./config/schemas/mesh.schema";
export type {
  SceneManagerConfig as SceneManagerConfigType,
  SceneOptions as SceneOptionsType,
} from "./config/schemas/scene.schema";
// Scene schemas
export {
  defaultSceneManagerConfig,
  defaultSceneOptions,
  SceneManagerConfigSchema,
  SceneOptionsSchema,
  safeValidateSceneManagerConfig,
  safeValidateSceneOptions,
  validateSceneManagerConfig,
  validateSceneOptions,
} from "./config/schemas/scene.schema";
// Config utilities
export { mergeConfigs } from "./config/utils";
// Inspector shared components (Fluent UI)
export type { StageSliderProps, VariantToggleGridProps } from "./inspector";
export { StageSlider, VariantToggleGrid } from "./inspector";
// Core manager infrastructure
// Note: SelectionBridgeServiceDefinition is NOT exported here to avoid loading
// @babylonjs/inspector at package import time. It's dynamically imported in BabylonCanvas.
export { BabylonManager } from "./managers/BabylonManager";
export { ConfigurationManager } from "./managers/config/ConfigurationManager";
export { BaseManager } from "./managers/core/BaseManager";
export { EventBus } from "./managers/core/EventBus";
// Core types
export type {
  BabylonConfig,
  EngineType,
  EventHandler,
  IDisposable,
  IManager,
  ManagerEvents,
  Unsubscribe,
} from "./managers/core/types";
// Type guards
export { isDisposable, isManager } from "./managers/core/types";
// Engine management
export { EngineManager } from "./managers/engine/EngineManager";
export type { AnyEngine, EngineOptions } from "./managers/engine/types";
export {
  isEngine,
  isNullEngine,
  isWebGPUEngine,
} from "./managers/engine/types";
// Interaction manager (selection + drag)
export { InteractionManager } from "./managers/interaction/InteractionManager";
export type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  InteractionOptions,
  MeshDeselectedEvent,
  MeshSelectedEvent,
  VisualEffectConfig,
} from "./managers/interaction/types";
export type {
  CollisionCallback,
  CollisionEvent,
  PhysicsAggregateInfo,
  PhysicsBodyOptions,
  PhysicsOptions,
  PhysicsRaycastResult,
  PhysicsShapeType,
} from "./managers/physics";
// Physics manager
export {
  defaultPhysicsBodyOptions,
  defaultPhysicsOptions,
  PhysicsBodyOptionsSchema,
  PhysicsManager,
  PhysicsOptionsSchema,
  PhysicsShapeTypeSchema,
  safeValidatePhysicsBodyOptions,
  safeValidatePhysicsOptions,
  Vector3Schema,
  validatePhysicsBodyOptions,
  validatePhysicsOptions,
} from "./managers/physics";
// Bob (Runescape cat) manager
export { BobManager } from "./managers/scene/BobManager";
export type { BobMesh } from "./managers/scene/bobData/types";
export { CameraManager } from "./managers/scene/CameraManager";
export type { DrillResult } from "./managers/scene/DrillManager";
// Drill (power tool) manager - loads from .glb file at runtime with animations
export { DrillManager } from "./managers/scene/DrillManager";
export { GemManager } from "./managers/scene/GemManager";
export { LightManager } from "./managers/scene/LightManager";
export { MaterialManager } from "./managers/scene/MaterialManager";
export { MeshManager } from "./managers/scene/MeshManager";
export type { NsResult } from "./managers/scene/NsManager";
// Narlock Systems logo manager - loads from .glb file at runtime
export { NsManager } from "./managers/scene/NsManager";
export type { PhysicsConfig } from "./managers/scene/PhysicsConfigManager";
// Physics config management (shared state for Inspector + React)
export { PhysicsConfigManager } from "./managers/scene/PhysicsConfigManager";
export type { RuneVariant } from "./managers/scene/RuneManager";
// Rune manager with composition helpers
export {
  DEFAULT_RUNE_STAGE_CONFIG,
  RUNE_VARIANTS,
  RuneManager,
} from "./managers/scene/RuneManager";
export type { RuneMesh } from "./managers/scene/runeData/types";
// Scene management
export { SceneManager } from "./managers/scene/SceneManager";
export type { StageConfig } from "./managers/scene/StageConfigManager";
// Stage config management (shared state for Inspector + React)
export { StageConfigManager } from "./managers/scene/StageConfigManager";
// Scene types
export type {
  MaterialConfig,
  MeshConfig,
  PBRMaterialConfig,
  SceneManagerConfig,
  SceneOptions,
  StandardMaterialConfig,
} from "./managers/scene/types";
export type { VisualEffectsConfig } from "./managers/scene/VisualEffectsConfigManager";
// Visual effects config management (shared state for Inspector + React)
export { VisualEffectsConfigManager } from "./managers/scene/VisualEffectsConfigManager";
export type {
  SharedAssets,
  StageAssetContext,
  StageErrorEvent,
  StageInteractionConfig,
  StageLoadedEvent,
  StagePack,
  StagePhysicsConfig,
  StageThemeInfo,
  StageUnloadedEvent,
} from "./managers/stage";
// Stage manager (hot-swap assets)
export { StageManager } from "./managers/stage";
export type {
  BabylonCanvasProps,
  BabylonContextValue,
  BabylonProviderProps,
  UseBabylonManagerReturn,
  UseEngineReturn,
  UseSceneReturn,
} from "./react";
// React integration
export {
  BabylonCanvas,
  BabylonConsumer,
  BabylonContext,
  BabylonProvider,
  useBabylonContext,
  useBabylonManager,
  useCamera,
  useEngine,
  useMesh,
  useScene,
} from "./react";
// Service pattern infrastructure (Inspector v2 style)
export type {
  IPhysicsConfigService,
  IStageConfigService,
  IVisualEffectsConfigService,
} from "./services/configServices";
export type { IService } from "./services/serviceIdentities";
export {
  PhysicsConfigServiceIdentity,
  StageConfigServiceIdentity,
  VisualEffectsConfigServiceIdentity,
} from "./services/serviceIdentities";
export type {
  TestEngineOptions,
  TestSceneOptions,
} from "./testing/NullEngineTestUtils";
// Test utilities
export {
  createTestEngine,
  createTestScene,
} from "./testing/NullEngineTestUtils";
// Utility functions
export { applySandboxCameraDefaults } from "./utils/applySandboxCameraDefaults";
